/**
 * Flight duration from LOCAL departure/arrival times across timezones.
 *
 * The Logistics tab stores departure/arrival as LOCAL times (HH:MM) at the
 * FROM/TO airports, plus the flight date. With the airports' IANA timezones
 * (airports.tz, backfilled from OpenFlights) the wall-clock difference is
 * corrected by the UTC-offset difference:
 *
 *   duration = arrival − departure + (offsetFrom − offsetTo)
 *
 * e.g. GVA 10:00 → JFK 12:00: offsets +2/+(-5) on a summer date →
 * 120 + 120 − (−300) … → 8h30. Overnight flights roll over midnight (mod 24h,
 * flights are < 24 h).
 */

/** Minutes east of UTC for `tz` at noon UTC on `date` (YYYY-MM-DD) — DST-aware. */
export function tzOffsetMinutes(tz: string, date: string): number {
  const noon = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(noon.getTime())) return 0;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = dtf.formatToParts(noon).reduce<Record<string, string>>((a, x) => { a[x.type] = x.value; return a; }, {});
  const hour = p.hour === '24' ? 0 : Number(p.hour);
  const asUTC = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second));
  return Math.round((asUTC - noon.getTime()) / 60000);
}

const toMin = (hhmm: string): number => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return NaN;
  return Number(m[1]) * 60 + Number(m[2]);
};

/**
 * Compute the flight duration as HH:MM, or '' when inputs are incomplete.
 * `date` = the flight date (YYYY-MM-DD) — used for the DST-correct offsets.
 */
export function computeFlightDuration(departure: string, arrival: string, fromTz: string, toTz: string, date: string): string {
  const dep = toMin(departure);
  const arr = toMin(arrival);
  if (Number.isNaN(dep) || Number.isNaN(arr) || !fromTz || !toTz || !date) return '';
  let raw = arr - dep + (tzOffsetMinutes(fromTz, date) - tzOffsetMinutes(toTz, date));
  while (raw < 0) raw += 1440; // overnight (flights are < 24 h)
  raw %= 1440;
  return `${String(Math.floor(raw / 60)).padStart(2, '0')}:${String(raw % 60).padStart(2, '0')}`;
}

/**
 * Train duration from LOCAL departure/arrival times (SPEC-TRAIN-LEG-FIXES).
 * Unlike flights there is NO timezone correction: the stations catalog has no
 * tz column, European trains stay in (almost always) one timezone and station
 * times are LOCAL to the station — so the plain wall-clock gap is correct.
 * Negative gap (crosses midnight, e.g. 23:10 → 00:40 = 01:30) rolls over +24h.
 */
export function computeTrainDuration(departure: string, arrival: string): string {
  const dep = toMin(departure);
  const arr = toMin(arrival);
  if (Number.isNaN(dep) || Number.isNaN(arr)) return '';
  let raw = arr - dep;
  if (raw < 0) raw += 1440; // overnight train (trains are < 24 h)
  return `${String(Math.floor(raw / 60)).padStart(2, '0')}:${String(raw % 60).padStart(2, '0')}`;
}
